import React, { useState, useEffect, useRef } from 'react'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface Field {
  type: string
  label: string
  options?: string[]
}

interface Step {
  title: string
  fields: Field[]
}

interface StepSelectorProps {
  steps: Step[]
  activeStep: number
  onStepSelect: (index: number) => void
  isStepValid: boolean
}

const StepSelector: React.FC<StepSelectorProps> = ({
  steps,
  activeStep,
  onStepSelect,
  isStepValid,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation()
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>(
    'bottom',
  )
  const selectorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen && selectorRef.current) {
      const rect = selectorRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      setDropdownPosition(
        spaceBelow < 300 && spaceAbove > spaceBelow ? 'top' : 'bottom',
      )
    }
  }, [isOpen])

  const handleStepClick = (index: number) => {
    if (activeStep < index && !isStepValid) return
    onStepSelect(index)
    setIsOpen(false)
  }

  return (
    <div className='relative w-full' ref={selectorRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className='w-full flex items-center justify-between bg-anthracite text-white p-3 rounded-lg'
        aria-expanded={isOpen}
        aria-haspopup='true'
      >
        <div className='flex items-center gap-3'>
          <span className='flex items-center justify-center w-7 h-7 bg-redpink rounded-full font-bold'>
            {activeStep + 1}
          </span>
          <span className='font-medium'>{steps[activeStep].title}</span>
        </div>
        <ChevronDown
          className={`w-5 h-5 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <>
          <div
            className={`absolute ${
              dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
            } left-0 right-0 z-50 bg-white rounded-lg shadow-lg border border-gray-200`}
          >
            <div className='max-h-[60vh] overflow-y-auto'>
              {steps.map((step, index) => {
                const isCompleted = activeStep > index
                const isCurrent = activeStep === index
                const isBlocked = activeStep < index && !isStepValid

                return (
                  <button
                    key={step.title}
                    onClick={() => handleStepClick(index)}
                    disabled={isBlocked}
                    className={`
                      w-full flex items-center gap-3 p-3
                      ${
                        index !== steps.length - 1
                          ? 'border-b border-gray-100'
                          : ''
                      }
                      ${
                        isBlocked
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-gray-50'
                      }
                      ${isCurrent ? 'bg-redpink/5' : ''}
                      transition-colors duration-200
                    `}
                  >
                    <span
                      className={`
                        flex items-center justify-center w-7 h-7 rounded-full font-bold
                        ${
                          isCompleted || isCurrent
                            ? 'bg-redpink text-white'
                            : 'bg-anthracite text-white'
                        }
                      `}
                    >
                      {index + 1}
                    </span>
                    <span
                      className={`${
                        isCurrent
                          ? 'text-redpink font-medium'
                          : 'text-anthracite'
                      }`}
                    >
                      {step.title}
                    </span>
                    {isCurrent && (
                      <span className='ml-auto text-xs bg-redpink text-white px-2 py-1 rounded'>
                        {t('current')}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
          <div
            className='fixed inset-0 bg-black/20 z-40'
            onClick={() => setIsOpen(false)}
            aria-hidden='true'
          />
        </>
      )}
    </div>
  )
}

export default StepSelector
